// Identity-mapping API surface (FD-098). Consumes the FD-097/FD-103 manager
// endpoints — contract rows in docs/TASKS.md API Contract Changes are the
// authoritative shapes; the mirrored interfaces live in src/types/identity.ts.

import { ApiError, getJson, postJsonWithStatus } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';
import {
  CONSENT_ERROR_CODES,
  ConsentErrorData,
  IdentityMappingTemplateData,
  IdentityMappingsListData,
  IdentitySuggestionsData,
  MappingSubmission,
  RemoveIdentityMappingsData,
  SetIdentityMappingsData,
  TenantRole,
  TenantUsersData,
} from '../types/identity';

// --- Consent-class 409 recognition (FD-096 shape) -----------------------------

// Returns the FD-096 consent payload when `err` is a consent-class 409
// (error.code TENANT_NOT_CONSENTED / TENANT_CONSENT_DRIFT), else null.
// Consent failures must never surface as raw error strings — callers render
// the tenant + missing roles + consent URL MessageBar instead.
export function extractConsentError(err: unknown): ConsentErrorData | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  if (!isApiEnvelope<Partial<ConsentErrorData>>(err.body)) return null;
  const code = err.body.error?.code;
  if (!code || !(CONSENT_ERROR_CODES as readonly string[]).includes(code)) return null;
  const data = err.body.data;
  if (!data || typeof data.tenantId !== 'string' || typeof data.consentUrl !== 'string') {
    return null;
  }
  return {
    tenantId: data.tenantId,
    failureClass: data.failureClass ?? code,
    missingRoles: Array.isArray(data.missingRoles) ? data.missingRoles : [],
    consentUrl: data.consentUrl,
  };
}

// True when `err` is the whole-request 502 GRAPH_ERROR the FD-097/FD-103
// endpoints return after their per-call retry-once. Rendered as a transient
// error state with a Retry action.
export function isGraphError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 502) return false;
  if (isApiEnvelope(err.body)) {
    return (err.body as ApiEnvelope<unknown>).error?.code === 'GRAPH_ERROR';
  }
  return true;
}

// Normalizes a manifest/device SID to the FD-103 join-key form: trimmed,
// leading `S` uppercased. This is key normalization for the device-grid ↔
// mapping-section join (FD-103 consumption notes: "join key = normalized
// SID"), not business logic — match verdicts come from the server.
export function normalizeSid(sid: string): string {
  const trimmed = sid.trim();
  return trimmed.startsWith('s') ? `S${trimmed.slice(1)}` : trimmed;
}

// --- GET /v1/migrations/{id}/identity-suggestions (FD-103 v1.0) ---------------

export interface GetIdentitySuggestionsParams {
  top?: number;
  /**
   * FD-506. Narrows the deduped user list server-side, BEFORE the page window,
   * against the device-reported userName / samName / displayName and the SID.
   * A term under 2 characters is ignored by the server rather than rejected.
   */
  search?: string | null;
  continuationToken?: string | null;
}

export async function getIdentitySuggestions(
  migrationId: string,
  params: GetIdentitySuggestionsParams = {},
): Promise<IdentitySuggestionsData> {
  const query = new URLSearchParams();
  if (params.top) query.set('top', String(params.top));
  // FD-506: sent trimmed, and only when it survives the trim — an empty
  // `search=` on the wire is a filter the server would have to decide to
  // ignore, and a parameter nobody has to interpret cannot be misinterpreted.
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) query.set('search', trimmedSearch);
  if (params.continuationToken) query.set('continuationToken', params.continuationToken);
  const qs = query.toString();
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/identity-suggestions` +
    (qs ? `?${qs}` : '');
  const envelope = await getJson<ApiEnvelope<IdentitySuggestionsData>>(path);
  return envelope.data;
}

// --- GET /v1/migrations/{id}/identity-mappings (FD-097 v1.0) -----------------

// FD-387 — the per-campaign mapping list. The endpoint has existed since FD-097
// and the frontend never had a client for it: the workspace built its rows
// exclusively from `identity-suggestions`, which derives them from devices that
// have submitted a manifest. A mapping for a user with no such device was
// therefore written successfully and then invisible — and that is precisely the
// case the manual "Add mapping" flow was built for, so the feature created rows
// its own view could not show.

export interface GetIdentityMappingsParams {
  top?: number;
  continuationToken?: string | null;
}

export async function getIdentityMappings(
  migrationId: string,
  params: GetIdentityMappingsParams = {},
): Promise<IdentityMappingsListData> {
  const search = new URLSearchParams();
  if (params.top) search.set('top', String(params.top));
  if (params.continuationToken) search.set('continuationToken', params.continuationToken);
  const qs = search.toString();
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/identity-mappings` +
    (qs ? `?${qs}` : '');
  const envelope = await getJson<ApiEnvelope<IdentityMappingsListData>>(path);
  return envelope.data;
}

// --- GET /v1/migrations/{id}/tenant-users (FD-097 v1.0 / FD-103 v1.1) ---------

export interface GetTenantUsersParams {
  tenant: TenantRole;
  search: string; // server-required, min 3 chars — callers gate before fetching
  top?: number;
  skipToken?: string | null;
}

export async function getTenantUsers(
  migrationId: string,
  params: GetTenantUsersParams,
): Promise<TenantUsersData> {
  const search = new URLSearchParams();
  search.set('tenant', params.tenant);
  search.set('search', params.search);
  if (params.top) search.set('top', String(params.top));
  if (params.skipToken) search.set('skipToken', params.skipToken);
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/tenant-users?${search.toString()}`;
  const envelope = await getJson<ApiEnvelope<TenantUsersData>>(path);
  return envelope.data;
}

// --- POST /v1/migrations/{id}/identity-mappings (FD-097 v1.0 / FD-103 v1.1) ---
//
// Status codes: 200 all accepted, 207 partial, 409 all rejected. The
// all-rejected 409 carries the same per-row bucket detail the UI wants to
// render (add-devices precedent), so it is recovered into a result — but a
// consent-class 409 (FD-096 shape, no buckets) is NOT recovered: it rethrows
// so callers render the consent MessageBar.

export interface SetIdentityMappingsResult {
  status: number;
  message: string | null;
  data: SetIdentityMappingsData;
}

export async function setIdentityMappings(
  migrationId: string,
  mappings: MappingSubmission[],
): Promise<SetIdentityMappingsResult> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/identity-mappings`;
  try {
    const { status, body: envelope } = await postJsonWithStatus<
      ApiEnvelope<SetIdentityMappingsData>,
      { mappings: MappingSubmission[] }
    >(path, { mappings });
    return { status, message: envelope.data.message ?? null, data: envelope.data };
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      extractConsentError(err) === null &&
      isApiEnvelope<SetIdentityMappingsData>(err.body) &&
      err.body.data &&
      Array.isArray(err.body.data.rejected)
    ) {
      return {
        status: err.status,
        message: err.body.error.message ?? null,
        data: err.body.data,
      };
    }
    throw err;
  }
}

// --- GET /v1/migrations/{id}/identity-mappings/template (FD-099) ---------------

// Fetches the per-type CSV template. Returns the raw data (migrationType,
// filename, csv) so the caller can trigger a browser download of `csv`.
export async function getIdentityMappingTemplate(
  migrationId: string,
): Promise<IdentityMappingTemplateData> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/identity-mappings/template`;
  const envelope = await getJson<ApiEnvelope<IdentityMappingTemplateData>>(path);
  return envelope.data;
}

// --- POST /v1/migrations/{id}/identity-mappings/import (FD-099) ----------------
//
// CSV import. Sends { csv } and returns the SAME bucket result as
// setIdentityMappings (200/207/409 recovered into a result). A whole-file
// 400 CSV_PARSE_ERROR is NOT recovered — it rethrows so the caller renders the
// header/parse message; consent-class 409 rethrows exactly as the JSON endpoint.

export async function importIdentityMappings(
  migrationId: string,
  csv: string,
): Promise<SetIdentityMappingsResult> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/identity-mappings/import`;
  try {
    const { status, body: envelope } = await postJsonWithStatus<
      ApiEnvelope<SetIdentityMappingsData>,
      { csv: string }
    >(path, { csv });
    return { status, message: envelope.data.message ?? null, data: envelope.data };
  } catch (err) {
    // Recover the 207/409-with-buckets shape (same as setIdentityMappings); a
    // consent-class 409 and the 400 CSV_PARSE_ERROR fall through to rethrow.
    if (
      err instanceof ApiError &&
      err.status === 409 &&
      extractConsentError(err) === null &&
      isApiEnvelope<SetIdentityMappingsData>(err.body) &&
      err.body.data &&
      Array.isArray(err.body.data.rejected)
    ) {
      return {
        status: err.status,
        message: err.body.error.message ?? null,
        data: err.body.data,
      };
    }
    throw err;
  }
}

// --- POST /v1/migrations/{id}/identity-mappings/remove (FD-103 v1.1) ----------
//
// Status codes: 200 all removed, 207 partial, 404 nothing removed. The 404
// body still carries the notFound bucket — recovered like remove-devices' 404.

export interface RemoveIdentityMappingsResult {
  status: number;
  message: string | null;
  data: RemoveIdentityMappingsData;
}

export async function removeIdentityMappings(
  migrationId: string,
  sourceKeys: string[],
): Promise<RemoveIdentityMappingsResult> {
  const path = `/v1/migrations/${encodeURIComponent(migrationId)}/identity-mappings/remove`;
  try {
    const { status, body: envelope } = await postJsonWithStatus<
      ApiEnvelope<RemoveIdentityMappingsData>,
      { sourceKeys: string[] }
    >(path, { sourceKeys });
    return { status, message: envelope.data.message ?? null, data: envelope.data };
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 404 &&
      isApiEnvelope<RemoveIdentityMappingsData>(err.body) &&
      err.body.data &&
      Array.isArray(err.body.data.notFound)
    ) {
      return {
        status: err.status,
        message: err.body.error.message ?? null,
        data: err.body.data,
      };
    }
    throw err;
  }
}
